export function searchAndReplace(str, find, replace) {
  return str.split(find).join(replace)
}

export function escapeHTML(str) {
  return str.replace(/<[^>]*\/?>([\s\S]*)?<\/.*?>/gi, function (match) {
    return match
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  })
}

export class GroupChatBuilder {
  /**
   * Create a group chat room builder.
   * @constructs
   * @param {RoomAdapter} roomAdapter - Room adapter to be used to call backend
   *  API.
   */
  constructor(roomAdapter) {
    this.roomAdapter = roomAdapter
    this.name = null
    this.emails = []
    this.options = {}
  }

  /**
   * Set the room name
   * @param {string} name - Room name
   * @returns {GroupChatBuilder}
   */
  withName(name) {
    this.name = name
    return this
  }

  /**
   * Add an options to this room.
   * @param {object} options - Any data that is `JSON.stringify` able
   * @returns {GroupChatBuilder}
   */
  withOptions(options) {
    this.options = options
    return this
  }

  /**
   * Add more participants to the room.
   * This method use javascript rest operator, which mean you can add as many as
   * you want.
   * eg: addParticipants('email1@gg.com', 'email2@gg.com')
   * @param {string} emails - Email of participant to be added.
   */
  addParticipants(...emails) {
    this.emails = this.emails
      .filter((email) => emails.indexOf(email) === -1)
      .concat(...emails)
    return this
  }

  /**
   * Real create group chat room by calling the backend API.
   * @returns {Promise.<Room, Error>}
   */
  create() {
    const name = this.name
    const emails = this.emails
    const options = this.options
    return this.roomAdapter.createRoom(
      name,
      emails,
      { avatarURL: options.avatarURL },
      options
    )
  }
}

export function scrollToBottom(latestCommentId) {
  requestAnimationFrame(function () {
    if (latestCommentId > 0) {
      const elementToScroll = document.getElementById(latestCommentId)
      if (!elementToScroll) return false
      elementToScroll.scrollIntoView({ block: 'end', behavior: 'smooth' })
    }
    // on entering the room, wait for data processed then focus on comment form
    document
      .getElementsByClassName('qcw-comment-form')
      .item(0)
      .getElementsByTagName('textarea')
      .item(0)
      .focus()
  })
}

/**
 * Cari index pesan yang sudah pernah diterima di dalam list comment.
 *
 * Payload dari server nyimpen client generated id di `unique_temp_id`,
 * sedangkan `Comment` nyimpennya di `unique_id`, jadi dua-duanya perlu
 * dibandingkan. Comment yang masih pending belum punya `id`, makanya ada null
 * check biar dua pending comment yang beda ga saling match.
 *
 * @param {Array<object>} comments - list comment yang sudah ada
 * @param {object} message - payload pesan dari realtime / sync
 * @return {number} index comment yang match, -1 kalau ga ketemu
 */
export function findCommentIndex(comments, message) {
  if (comments == null || message == null) return -1

  const messageId = message.id
  const messageUniqueId = message.unique_temp_id ?? message.unique_id

  return comments.findIndex(
    (it) =>
      (messageId != null && it.id === messageId) ||
      (messageUniqueId != null && it.unique_id === messageUniqueId)
  )
}

export function delayed(cb, timeout) {
  let timer = null
  return function (...args) {
    if (timer != null) clearTimeout(timer)
    timer = setTimeout(() => {
      cb(...args)
      timer = null
    }, timeout)
  }
}

export const DEFAULT_BROKER_LB_URL = 'https://realtime-lb.qiscus.com'

/**
 * Whether an app config's `broker_lb_url` points at a load balancer we can
 * trust for this app.
 *
 * A blank value means the backend has nothing configured, and the shared
 * default means it was never set for this app — pointing an on-premise app at
 * the public lb would hand out broker nodes it cannot reach. Anything else is
 * the app's own lb.
 */
export function isCustomBrokerLbUrl(url) {
  if (typeof url !== 'string') return false
  const trimmed = url.trim().replace(/\/+$/, '')
  if (trimmed.length === 0) return false
  return trimmed !== DEFAULT_BROKER_LB_URL
}

/**
 * Decide whether the realtime load balancer should be enabled for this
 * `init()` call.
 *
 * A custom `baseURL`/`mqttURL` without a matching custom `brokerLbURL` is a
 * strong hint the app is self-hosted and the shared lb would hand out nodes
 * it cannot reach, so we guess the lb should be off. But that is only a
 * guess — an explicit `enableRealtimeLB` from the caller always wins over it,
 * in either direction.
 */
export function resolveEnableLb({
  hasCustomBaseUrl,
  hasCustomMqttUrl,
  hasCustomBrokerLbUrl,
  enableRealtimeLB,
  currentEnableLb,
}) {
  if (enableRealtimeLB != null) {
    return { enableLb: enableRealtimeLB, disabledByGuess: false }
  }

  const heuristicDisablesLb =
    (hasCustomBaseUrl || hasCustomMqttUrl) && !hasCustomBrokerLbUrl
  if (heuristicDisablesLb) {
    return { enableLb: false, disabledByGuess: true }
  }

  return { enableLb: currentEnableLb, disabledByGuess: false }
}
